/**
 * Overview Page (Mission Control)
 *
 * Condensed dashboard view with:
 * - 4 sparkline stat cards (processed, latency, PII detected, injections blocked)
 * - Compact pipeline status track
 * - Last 10 events stream
 * - Service health traffic-light grid
 * - InputArea (unchanged)
 */

import { lazy, Suspense, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  Clock,
  Eye,
  ScrollText,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import { PipelineWidget } from "../components/dashboard/PipelineWidget";
import { EventStreamWidget } from "../components/dashboard/EventStreamWidget";
import { WidgetSkeleton } from "../components/dashboard/WidgetSkeleton";
import { InputArea } from "../components/input/InputArea";
import { DemoReviewPanel } from "../components/pipeline/DemoReviewPanel";
import { ReviewPanel } from "../components/pipeline/ReviewPanel";
import { RecommendationPanel } from "../components/recommendation/RecommendationPanel";
import { useDemoPipeline } from "../demo/useDemoPipeline";
import { useEventStream } from "../hooks/useEventStream";
import { useLivePipeline } from "../hooks/useLivePipeline";
import { useAuditStats } from "../hooks/useAuditStats";
import { useSecurityStats } from "../hooks/useSecurityStats";
import { useRecommendations } from "../hooks/useRecommendations";
import { useDataMode } from "../providers/DataModeProvider";
import { useAuth } from "../providers/AuthProvider";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { WidgetErrorFallback } from "../components/dashboard/WidgetErrorFallback";
import {
  generateMetrics,
  generateEventStream,
  generateSystemHealth,
} from "../demo/generators";
import type { MetricsData, SystemHealth } from "../types/metrics";

const MetricsWidget = lazy(() =>
  import("../components/dashboard/MetricsWidget").then((m) => ({
    default: m.MetricsWidget,
  })),
);

export function OverviewPage() {
  const { mode, apiReachable } = useDataMode();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: auditStats } = useAuditStats();
  const { data: securityStats } = useSecurityStats();
  const demoPipeline = useDemoPipeline();
  const livePipeline = useLivePipeline();
  // Public mode uses demo pipeline (visitors can't create notes).
  // Live mode uses real pipeline. Demo mode uses demo pipeline.
  const pipeline = mode === "live" ? livePipeline : demoPipeline;

  // Recommendations — fires when a live pipeline note reaches COMPLETED.
  // The hook is disabled (returns empty) until noteId is set and note completes.
  const completedNoteId =
    mode === "live" &&
    livePipeline.pipelineState?.stages.STORAGE === "completed"
      ? livePipeline.noteId
      : undefined;
  const {
    recommendations,
    isLoading: recsLoading,
    rate: rateRec,
  } = useRecommendations(completedNoteId);

  // Metrics: in live/public mode, derive scalar values from audit stats.
  // Time-series arrays (piiDetections, llmLatency, tokenUsage) have no backend
  // source — they require Prometheus/OTEL. Demo generators provide sparkline data.
  const [demoMetrics, setDemoMetrics] = useState<MetricsData>(generateMetrics);
  const metrics = useMemo<MetricsData>(() => {
    if (mode === "demo") return demoMetrics;
    return {
      ...demoMetrics, // sparkline time-series from demo (no backend source)
      totalProcessed: auditStats?.totalEvents ?? demoMetrics.totalProcessed,
      avgLatencyMs: demoMetrics.avgLatencyMs, // no backend latency aggregation
    };
  }, [mode, auditStats, demoMetrics]);

  // System health: in live mode, set API service status from real health check.
  // Other services remain demo data (no multi-service health endpoint exists).
  const [demoHealth, setDemoHealth] =
    useState<SystemHealth>(generateSystemHealth);
  const systemHealth = useMemo<SystemHealth>(() => {
    if (mode === "demo") return demoHealth;
    return {
      ...demoHealth,
      services: demoHealth.services.map((svc) =>
        svc.name === "API"
          ? {
              ...svc,
              status: apiReachable ? "healthy" : "down",
              latencyMs: apiReachable ? svc.latencyMs : 0,
            }
          : svc,
      ),
    };
  }, [mode, demoHealth, apiReachable]);

  const { events: kafkaEvents } = useEventStream();
  const [demoEvents] = useState(generateEventStream);
  const mergedEvents =
    mode === "live"
      ? [...pipeline.events, ...kafkaEvents]
      : [...pipeline.events, ...demoEvents];

  // Refresh demo data periodically for sparkline animation
  useEffect(() => {
    const interval = setInterval(() => {
      setDemoMetrics(generateMetrics());
      setDemoHealth(generateSystemHealth());
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Derive display status from pipeline state.
  // isReviewing works for both demo and live modes — the old code only checked
  // demo mode, so live pipelines at REVIEW stage showed "PROCESSING" instead.
  const pipelineStatus = pipeline.isRunning
    ? pipeline.isReviewing
      ? "REVIEWING"
      : "PROCESSING"
    : pipeline.pipelineState
      ? pipeline.pipelineState.stages.GUARDRAILS === "failed"
        ? "FAILED"
        : pipeline.pipelineState.stages.STORAGE === "completed"
          ? "COMPLETED"
          : "PENDING"
      : undefined;

  return (
    <div className="p-2 sm:p-3 lg:p-4 space-y-2 sm:space-y-3">
      {/* Stat cards row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        <StatCard
          label="Recent Events"
          // Backend now bounds this count to events since the last audit
          // CHECKPOINT (append-only log never deletes rows), so it is a recent
          // window — not an all-time total. Data wiring is unchanged:
          // auditStats.totalEvents still feeds metrics.totalProcessed.
          help="Audit events recorded since the last checkpoint (recent window, not an all-time total)"
          value={metrics.totalProcessed}
          icon={<Activity className="w-3.5 h-3.5" />}
          sparklineData={metrics.piiDetections.map((p) => p.value)}
          color="var(--accent-cyan)"
        />
        <StatCard
          label="Avg Latency"
          value={`${metrics.avgLatencyMs}ms`}
          icon={<Clock className="w-3.5 h-3.5" />}
          sparklineData={metrics.llmLatency.map((p) => p.value)}
          color="var(--accent-blue)"
        />
        <StatCard
          label="PII Detected"
          value={securityStats.piiDetected}
          icon={<Eye className="w-3.5 h-3.5" />}
          sparklineData={metrics.piiDetections.map((p) => p.value)}
          color="var(--accent-yellow)"
        />
        <StatCard
          label="Inj. Blocked"
          value={securityStats.injectionsBlocked}
          icon={<ShieldAlert className="w-3.5 h-3.5" />}
          color="var(--accent-red)"
        />
      </div>

      {/* Pipeline + Event Stream */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 sm:gap-3">
        <div className="md:col-span-3">
          <PipelineWidget
            pipelineState={pipeline.pipelineState}
            noteStatus={pipelineStatus}
          />
        </div>
        <div className="md:col-span-2 min-h-[200px] sm:min-h-[260px]">
          <EventStreamWidget events={mergedEvents.slice(0, 10)} />
        </div>
      </div>

      {/* Review Panel — shown when pipeline pauses at REVIEW stage */}
      {mode === "demo" &&
        demoPipeline.isReviewing &&
        demoPipeline.demoReviewData && (
          <DemoReviewPanel
            draftSummary={demoPipeline.demoReviewData.summary}
            draftTags={demoPipeline.demoReviewData.tags}
            onReview={demoPipeline.completeReview}
          />
        )}
      {/* Live Review Panel — uses real API reviewNote() for Draft→Confirm→Commit */}
      {mode === "live" && livePipeline.isReviewing && livePipeline.note && (
        <ReviewPanel
          note={livePipeline.note}
          onReviewComplete={() => {
            // Invalidate the note query so useNotePipeline refetches and
            // advances the pipeline state past REVIEW → COMPLETED
            queryClient.invalidateQueries({
              queryKey: ["note", livePipeline.noteId],
            });
          }}
        />
      )}

      {/* Recommendation Panel — shown after live pipeline completes */}
      {completedNoteId && (
        <RecommendationPanel
          noteId={completedNoteId}
          recommendations={recommendations}
          isLoading={recsLoading}
          onRate={rateRec}
        />
      )}

      {/* Metrics + Service health + Audit */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3">
        <ErrorBoundary
          fallback={(reset) => <WidgetErrorFallback onRetry={reset} />}
        >
          <Suspense fallback={<WidgetSkeleton variant="chart" />}>
            <MetricsWidget data={metrics} />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary
          fallback={(reset) => <WidgetErrorFallback onRetry={reset} />}
        >
          <ServiceHealthGrid data={systemHealth} />
        </ErrorBoundary>
        <ErrorBoundary
          fallback={(reset) => <WidgetErrorFallback onRetry={reset} />}
        >
          <AuditWidget
            stats={auditStats}
            onNavigate={() => navigate("/audit")}
          />
        </ErrorBoundary>
      </div>

      {/* Input Area — hidden for anonymous visitors (creating notes requires auth) */}
      {isAuthenticated && (
        <InputArea
          onSubmit={pipeline.startPipeline}
          onSubmitAudio={pipeline.startAudioPipeline}
          isProcessing={pipeline.isRunning}
        />
      )}
    </div>
  );
}

/* ─── Inline Components ────────────────────────────────────────────── */

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  sparklineData?: number[];
  color: string;
  /** Optional hover tooltip clarifying what the metric measures */
  help?: string;
}

function StatCard({
  label,
  value,
  icon,
  sparklineData,
  color,
  help,
}: StatCardProps) {
  return (
    <div className="widget">
      <div className="px-3 py-2.5 flex items-center justify-between">
        <div className="space-y-1">
          <div
            className="flex items-center gap-1.5 text-text-muted"
            title={help}
          >
            {icon}
            <span className="text-xs">{label}</span>
          </div>
          <div className="text-lg font-bold text-text-primary font-mono">
            {value}
          </div>
        </div>
        {sparklineData && sparklineData.length > 1 && (
          <Sparkline data={sparklineData} color={color} />
        )}
      </div>
    </div>
  );
}

function Sparkline({ data, color }: { data: number[]; color: string }) {
  const width = 72;
  const height = 28;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  const points = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - 2 - ((v - min) / range) * (height - 4);
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg
      width={width}
      height={height}
      className="shrink-0 opacity-80"
      viewBox={`0 0 ${width} ${height}`}
    >
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const STATUS_DOT_COLORS: Record<string, string> = {
  healthy: "bg-accent-green",
  degraded: "bg-accent-yellow",
  down: "bg-accent-red",
};

function ServiceHealthGrid({ data }: { data: SystemHealth }) {
  return (
    <div className="widget">
      <div className="widget-header">
        <div className="flex items-center gap-2">
          <Activity className="w-3.5 h-3.5 text-accent-cyan" />
          <h3 className="text-xs font-semibold text-text-primary">
            Service Health
          </h3>
        </div>
        <span className="text-xs text-text-muted">
          {data.services.filter((s) => s.status === "healthy").length}/
          {data.services.length} healthy
        </span>
      </div>
      <div className="widget-body">
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {data.services.map((service) => (
            <div
              key={service.name}
              className="bg-bg-primary rounded-lg p-2 text-center space-y-1"
            >
              <span
                className={`inline-block w-2 h-2 rounded-full ${STATUS_DOT_COLORS[service.status]}`}
              />
              <div className="text-xs font-medium text-text-primary">
                {service.name}
              </div>
              <div className="text-xs text-text-muted font-mono">
                {service.latencyMs}ms
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AuditWidget({
  stats,
  onNavigate,
}: {
  stats: import("../types/audit").AuditStats | undefined;
  onNavigate: () => void;
}) {
  const relTime = (iso: string | null) => {
    if (!iso) return "—";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <button onClick={onNavigate} className="widget text-left w-full">
      <div className="widget-header">
        <div className="flex items-center gap-2">
          <ScrollText className="w-3.5 h-3.5 text-accent-cyan" />
          <h3 className="text-xs font-semibold text-text-primary">Audit Log</h3>
        </div>
      </div>
      <div className="widget-body">
        {stats ? (
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-lg font-bold text-text-primary font-mono">
                {stats.totalEvents.toLocaleString()}
              </div>
              <div className="text-[10px] text-text-muted">Events</div>
            </div>
            <div>
              <div
                className={`text-lg font-bold font-mono ${
                  stats.chainIntact ? "text-accent-green" : "text-accent-red"
                }`}
              >
                {stats.chainIntact ? (
                  <ShieldCheck className="w-5 h-5 mx-auto" />
                ) : (
                  "Broken"
                )}
              </div>
              <div className="text-[10px] text-text-muted">Chain</div>
            </div>
            <div>
              <div className="text-lg font-bold text-text-primary font-mono">
                {relTime(stats.newestEvent)}
              </div>
              <div className="text-[10px] text-text-muted">Latest</div>
            </div>
          </div>
        ) : (
          <div className="text-xs text-text-muted animate-pulse text-center py-4">
            Loading...
          </div>
        )}
      </div>
    </button>
  );
}
